import { useEffect, useState } from "react";
import { getAdminHeaders } from "./adminApi";

export default function AdminCitySeo() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [level, setLevel] = useState("");
  const [enabled, setEnabled] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [message, setMessage] = useState("");

  const pageSize = 20;

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (appliedKeyword) params.set("keyword", appliedKeyword);
      if (level) params.set("level", level);
      if (enabled !== "") params.set("enabled", enabled);

      const res = await fetch(`/api/admin/city-seo?${params.toString()}`, {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [page, appliedKeyword, level, enabled]);

  const runSeed = async () => {
    if (!window.confirm("从全国行政区划同步省/市/区县数据？已存在的记录默认不会覆盖 meta。")) {
      return;
    }
    const res = await fetch(
      "/api/admin/city-seo/seed?force_update=true&enable_all=true&refresh_meta=true",
      {
      method: "POST",
      headers: getAdminHeaders(),
    });
    const data = await res.json();
    setMessage(
      `${data.message || "同步完成"}（新增 ${data.created || 0}，启用 ${data.enabled || 0}）`
    );
    loadItems();
  };

  const runEnableAll = async () => {
    if (!window.confirm("启用全部省/市/区县 SEO 页面？")) return;
    const res = await fetch("/api/admin/city-seo/enable-all", {
      method: "POST",
      headers: getAdminHeaders(),
    });
    const data = await res.json();
    setMessage(data.message || `已启用 ${data.enabled || 0} 条`);
    loadItems();
  };

  const runRefreshMeta = async () => {
    if (!window.confirm("刷新全部省/市/县页面的 SEO 标题、描述与关键词？")) return;
    const res = await fetch("/api/admin/city-seo/refresh-meta", {
      method: "POST",
      headers: getAdminHeaders(),
    });
    const data = await res.json();
    setMessage(data.message || `已刷新 ${data.meta_refreshed || 0} 条`);
    loadItems();
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      slug: item.slug,
      short_name: item.short_name,
      local_hint: item.local_hint || "",
      meta_title: item.title || "",
      meta_description: item.meta_description || "",
      meta_keywords: item.keywords || "",
      tier: item.tier,
      is_enabled: item.is_enabled,
      sort_order: item.sort_order,
    });
  };

  const saveEdit = async () => {
    const res = await fetch(`/api/admin/city-seo/${editing.id}`, {
      method: "PATCH",
      headers: getAdminHeaders(true),
      body: JSON.stringify({
        ...form,
        is_enabled: Number(form.is_enabled),
        tier: Number(form.tier),
        sort_order: Number(form.sort_order),
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.detail || "保存失败");
      return;
    }
    setEditing(null);
    setMessage("已保存");
    loadItems();
  };

  const bulkEnableDistricts = async (value) => {
    const label = value ? "启用" : "停用";
    if (!window.confirm(`批量${label}所有区县级页面？`)) return;
    const res = await fetch(
      `/api/admin/city-seo/bulk-enable?level=district&is_enabled=${value}`,
      { method: "POST", headers: getAdminHeaders() }
    );
    const data = await res.json();
    setMessage(`区县批量${label}：${data.updated || 0} 条`);
    loadItems();
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">城市 SEO 管理</h1>
          <p className="text-sm text-gray-500 mt-1">
            程序自动从全国区划生成页面；您在此启用/停用、修改 meta 文案与 URL。
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={runSeed}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
          >
            同步城市数据
          </button>
          <button
            onClick={runRefreshMeta}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm"
          >
            刷新全部关键词
          </button>
          <button
            onClick={runEnableAll}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm"
          >
            启用全部页面
          </button>
          <button
            onClick={() => bulkEnableDistricts(1)}
            className="px-4 py-2 border rounded-lg text-sm"
          >
            批量启用区县
          </button>
          <button
            onClick={() => bulkEnableDistricts(0)}
            className="px-4 py-2 border rounded-lg text-sm"
          >
            批量停用区县
          </button>
        </div>
      </div>

      {message && (
        <div className="bg-green-50 text-green-700 text-sm px-4 py-2 rounded-lg">{message}</div>
      )}

      <form
        className="bg-white rounded-xl shadow p-4 flex flex-wrap gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setAppliedKeyword(keyword.trim());
        }}
      >
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索城市/省份/slug"
          className="border rounded-lg px-3 py-2 text-sm flex-1 min-w-[180px]"
        />
        <select
          value={level}
          onChange={(e) => {
            setLevel(e.target.value);
            setPage(1);
          }}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">全部层级</option>
          <option value="province">省级</option>
          <option value="city">地级市</option>
          <option value="district">区县</option>
        </select>
        <select
          value={enabled}
          onChange={(e) => {
            setEnabled(e.target.value);
            setPage(1);
          }}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">全部状态</option>
          <option value="1">已启用</option>
          <option value="0">已停用</option>
        </select>
        <button type="submit" className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm">
          搜索
        </button>
      </form>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left p-3">城市</th>
              <th className="text-left p-3">层级</th>
              <th className="text-left p-3">slug</th>
              <th className="text-left p-3">状态</th>
              <th className="text-left p-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-gray-400">
                  加载中...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-gray-400">
                  暂无数据，请先点击「同步城市数据」
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="p-3">
                    <div className="font-medium">{item.short_name}</div>
                    <div className="text-xs text-gray-400">
                      {[item.province, item.city, item.district].filter(Boolean).join(" / ")}
                    </div>
                  </td>
                  <td className="p-3">{item.level === "district" ? "区县" : "城市"}</td>
                  <td className="p-3">
                    <a
                      href={`/city/${item.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {item.slug}
                    </a>
                  </td>
                  <td className="p-3">
                    {item.is_enabled ? (
                      <span className="text-green-600">启用</span>
                    ) : (
                      <span className="text-gray-400">停用</span>
                    )}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => openEdit(item)}
                      className="text-pink-600 hover:underline"
                    >
                      编辑
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-3">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            上一页
          </button>
          <span className="text-sm text-gray-500 self-center">
            {page}/{totalPages}（共 {total} 条）
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            下一页
          </button>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <h2 className="text-xl font-bold">编辑 · {editing.short_name}</h2>
            <p className="text-xs text-gray-400">
              meta 标题/描述/关键词仅用于搜索引擎，不会在页面正文显示。
            </p>

            {[
              ["slug", "URL slug"],
              ["short_name", "展示简称"],
              ["local_hint", "本地说明"],
              ["meta_title", "SEO 标题"],
              ["meta_description", "SEO 描述"],
              ["meta_keywords", "SEO 关键词"],
            ].map(([key, label]) => (
              <label key={key} className="block text-sm">
                <span className="text-gray-600">{label}</span>
                {key.includes("description") || key === "local_hint" ? (
                  <textarea
                    className="mt-1 w-full border rounded-lg p-2"
                    rows={3}
                    value={form[key] || ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  />
                ) : (
                  <input
                    className="mt-1 w-full border rounded-lg p-2"
                    value={form[key] || ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  />
                )}
              </label>
            ))}

            <div className="grid grid-cols-3 gap-3">
              <label className="text-sm">
                层级权重 tier
                <input
                  type="number"
                  className="mt-1 w-full border rounded-lg p-2"
                  value={form.tier}
                  onChange={(e) => setForm({ ...form, tier: e.target.value })}
                />
              </label>
              <label className="text-sm">
                排序
                <input
                  type="number"
                  className="mt-1 w-full border rounded-lg p-2"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                />
              </label>
              <label className="text-sm">
                启用
                <select
                  className="mt-1 w-full border rounded-lg p-2"
                  value={form.is_enabled}
                  onChange={(e) => setForm({ ...form, is_enabled: e.target.value })}
                >
                  <option value={1}>启用</option>
                  <option value={0}>停用</option>
                </select>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 border rounded-lg text-sm"
              >
                取消
              </button>
              <button
                onClick={saveEdit}
                className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
